<template>
    <IntegrationsTabs tab-key="available">
        <div
            v-if="!canCreate"
            class="my-10 rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
        >
            Your role on this team does not allow adding integrations. Ask a team
            owner or administrator to add one for you.
        </div>

        <div
            v-else
            class="my-10 flex w-full flex-wrap gap-x-4 gap-y-8 md:grid md:grid-cols-2 lg:grid-cols-3"
        >
            <!-- Lettr: admin-only while the integration is rolled out internally -->
            <LettrIntegrationBox
                v-if="isTopolAdmin"
                @openNew="lettrIntegrationModalOpened = true"
            />

            <SendGridIntegrationBox
                @openNew="sendgridIntegrationModalOpened = true"
            />

            <SendyIntegrationBox
                @openNew="sendyIntegrationModalOpened = true"
            />

            <BrevoIntegrationBox
                @openNew="brevoIntegrationModalOpened = true"
            />

            <ConstantContactIntegrationBox
                @openNew="constantcontactIntegrationModalOpened = true"
            />

            <MailchimpIntegrationBox
                @openNew="mailchimpIntegrationModalOpened = true"
            />

            <KlaviyoIntegrationBox
                @openNew="klaviyoIntegrationModalOpened = true"
            />

            <MailjetIntegrationBox
                @openNew="mailjetIntegrationModalOpened = true"
            />

            <GmailIntegrationBox
                v-if="!featureDisabled('gmail')"
                :disabled="featureDisabled('gmail')"
                @openNew="gmailIntegrationModalOpened = true"
            />

            <HubspotIntegrationBox
                @openNew="hubspotIntegrationModalOpened = true"
            />

            <CampaignMonitorIntegrationBox
                @openNew="campaignmonitorIntegrationModalOpened = true"
            />

            <ActiveCampaignIntegrationBox
                @openNew="activecampaignIntegrationModalOpened = true"
            />

            <MoosendIntegrationBox
                @openNew="moosendIntegrationModalOpened = true"
            />

            <ClickSendIntegrationBox
                @openNew="clicksendIntegrationModalOpened = true"
            />

            <!-- BenchmarkEmail -->
            <BenchmarkEmailIntegrationBox
                @openNew="benchmarkemailIntegrationModalOpened = true"
            />

            <!-- Mailgun -->
            <MailgunIntegrationBox
                @openNew="mailgunIntegrationModalOpened = true"
            />

            <!-- AWeber -->
            <AWeberIntegrationBox
                @openNew="aweberIntegrationModalOpened = true"
            />

            <!-- MailUp -->
            <MailUpIntegrationBox
                @openNew="mailupIntegrationModalOpened = true"
            />

            <!-- MailerLite -->
            <MailerLiteIntegrationBox
                @openNew="mailerliteIntegrationModalOpened = true"
            />

            <!-- Acumbamail -->
            <AcumbamailIntegrationBox
                @openNew="acumbamailIntegrationModalOpened = true"
            />

            <!-- GetResponse -->
            <GetResponseIntegrationBox
                @openNew="getresponseIntegrationModalOpened = true"
            />

            <!-- ZohoCampaigns -->
            <ZohoCampaignsIntegrationBox
                @openNew="zohocampaignsIntegrationModalOpened = true"
            />

            <!-- ZohoMail -->
            <ZohoMailIntegrationBox
                @openNew="zohomailIntegrationModalOpened = true"
            />

            <!-- ZohoMarketing -->
            <ZohoMarketingIntegrationBox
                @openNew="zohomarketingIntegrationModalOpened = true"
            />

            <!-- SendPulse -->
            <SendPulseIntegrationBox
                @openNew="sendpulseIntegrationModalOpened = true"
            />

            <!-- OutlookWeb -->
            <OutlookWebIntegrationBox
                @openNew="outlookwebIntegrationModalOpened = true"
            />

            <!-- Close -->
            <CloseIntegrationBox
                @openNew="closeIntegrationModalOpened = true"
            />

            <!-- iContact -->
            <IContactIntegrationBox
                @openNew="icontactIntegrationModalOpened = true"
            />

            <!-- CleverReach -->
            <CleverReachIntegrationBox
                @openNew="cleverReachIntegrationModalOpened = true"
            />
        </div>
    </IntegrationsTabs>
</template>

<script lang="ts" setup>

import { computed, onMounted } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import useIntegrations from '../composables/useIntegrations';
import usePermissions from '@/composables/usePermissions';
import { CONNECT_QUERY_PARAM } from '@/composables/useLettrPromo';

const { canCreate } = usePermissions();

/** Topol staff, not the team `admin` role that `usePermissions` reports. */
const isTopolAdmin = computed(() => usePage<ITopolPageProps>().props.isAdmin);

const props = defineProps({
    features: {
        type: Object,
        default: null,
    },
});

const {
    sendgridIntegrationModalOpened,
    sendyIntegrationModalOpened,
    brevoIntegrationModalOpened,
    constantcontactIntegrationModalOpened,
    mailchimpIntegrationModalOpened,
    klaviyoIntegrationModalOpened,
    mailjetIntegrationModalOpened,
    gmailIntegrationModalOpened,
    hubspotIntegrationModalOpened,
    campaignmonitorIntegrationModalOpened,
    activecampaignIntegrationModalOpened,
    moosendIntegrationModalOpened,
    clicksendIntegrationModalOpened,
    benchmarkemailIntegrationModalOpened,
    mailgunIntegrationModalOpened,
    aweberIntegrationModalOpened,
    mailupIntegrationModalOpened,
    mailerliteIntegrationModalOpened,
    acumbamailIntegrationModalOpened,
    getresponseIntegrationModalOpened,
    zohocampaignsIntegrationModalOpened,
    zohomailIntegrationModalOpened,
    zohomarketingIntegrationModalOpened,
    sendpulseIntegrationModalOpened,
    outlookwebIntegrationModalOpened,
    closeIntegrationModalOpened,
    icontactIntegrationModalOpened,
    cleverReachIntegrationModalOpened,
    lettrIntegrationModalOpened,
} = useIntegrations();

/**
 * `?connect=lettr` opens the Lettr modal straight away, so a Connect button
 * elsewhere in the app lands on the form rather than on the grid.
 *
 * Gated the same way the box itself is: someone who cannot add an integration,
 * or who is not Topol staff while Lettr rolls out, gets the page as it is.
 */
onMounted(() => {
    const requested = new URLSearchParams(window.location.search).get(
        CONNECT_QUERY_PARAM
    );

    if (requested !== 'lettr' || !canCreate.value || !isTopolAdmin.value) {
        return;
    }

    lettrIntegrationModalOpened.value = true;
});

function featureDisabled(value: string) {
    if (!props.features) {
        return true;
    } else {
        //@ts-ignore
        return !props.features[value];
    }
}
</script>
